import * as React from "react";
import {
  Button,
  Callout,
  Classes,
  H3,
  Intent,
  Overlay2 as Overlay,
  TextArea,
  FormGroup,
  InputGroup,
} from "@blueprintjs/core";
import { useParams } from "react-router-dom";
import { FormAttachment } from "components/common";
import { useSupportState, useSupportActions } from "context/support";
import { useAuthState } from "context/auth";
import ApiMessaging from "../../api/messaging";

function Support() {
  const { id: surveyId } = useParams();
  const { currentUser } = useAuthState();
  const { isOpen } = useSupportState();
  const { close } = useSupportActions();
  const [subject, setSubject] = React.useState("");
  const [details, setDetails] = React.useState("");
  const [files, setFiles] = React.useState([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const reset = () => {
    setSubject("");
    setDetails("");
    setFiles([]);
    setError(null);
  };

  const handleSubmit = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();

    if (!subject || !details || isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await ApiMessaging.supportRequest(
        currentUser,
        subject,
        details,
        files,
        surveyId
      );
      reset();
      close();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to submit support request";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    reset();
    close();
  };

  const isSurvey = surveyId !== undefined;
  const isSubmitDisabled = !subject || !details || isSubmitting;

  return (
    <Overlay
      onClose={handleClose}
      className={Classes.OVERLAY_SCROLL_CONTAINER}
      isOpen={isOpen}
      hasBackdrop
      usePortal
      canOutsideClickClose={!isSubmitting}
      canEscapeKeyClose={!isSubmitting}
      autoFocus
    >
      <div
        className={`bp6-dark ${Classes.CARD} ${Classes.ELEVATION_4} support-overlay`}
      >
        <H3>We're Here to Help</H3>
        <p>This form will allow you to create a customer support ticket.</p>
        <br />
        <form onSubmit={handleSubmit}>
          {isSurvey && (
            <FormGroup label="Survey" labelFor="survey-input">
              <InputGroup id="survey-input" value={surveyId} disabled />
            </FormGroup>
          )}
          <FormGroup
            label="Subject"
            labelFor="subject-input"
            labelInfo="(required)"
          >
            <InputGroup
              id="subject-input"
              value={subject}
              onChange={(event) => {
                setSubject(event.target.value);
                setError(null);
              }}
              disabled={isSubmitting}
            />
          </FormGroup>
          <FormGroup
            label="Details"
            labelFor="details-input"
            labelInfo="(required)"
          >
            <TextArea
              id="details-input"
              size="large"
              fill
              value={details}
              onChange={(event) => {
                setDetails(event.target.value);
                setError(null);
              }}
              disabled={isSubmitting}
            />
          </FormGroup>
          <FormAttachment files={files} setFiles={setFiles} />
          {error && (
            <Callout intent={Intent.DANGER} style={{ marginBottom: 10 }}>
              {error}
            </Callout>
          )}
          <div className={Classes.DIALOG_FOOTER_ACTIONS}>
            <Button
              intent={Intent.DANGER}
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              intent={Intent.SUCCESS}
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitDisabled}
            >
              Submit
            </Button>
          </div>
        </form>
      </div>
    </Overlay>
  );
}

export default Support;
